/**
 * Stream B — B65: Contact Enrichment Settings
 *
 * Configure enrichment providers: Clearbit, Apollo, Hunter.io.
 * Waterfall order, API key management, usage tracking.
 * Real apiFetch. No mocks.
 */
import { useState, useEffect, useCallback, type FormEvent } from "react";
import { apiFetch } from "@/lib/api-client";
import { Sparkles, Key, ArrowDown, CheckCircle2, XCircle, Settings2 } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface EnrichmentProvider {
  id: string;
  name: string;
  description: string;
  isConfigured: boolean;
  isEnabled: boolean;
  priority: number;
  usageThisMonth: number;
  monthlyLimit: number | null;
}

interface EnrichmentConfig {
  providers: EnrichmentProvider[];
  autoEnrich: boolean;
  enrichOnCreate: boolean;
  enrichOnImport: boolean;
}

const PROVIDER_INFO: Record<string, { description: string }> = {
  clearbit: { description: "Company & contact data from Clearbit. Best for B2B SaaS contacts." },
  apollo: { description: "Contact search and enrichment from Apollo.io. Large B2B database." },
  hunter: { description: "Email verification and company enrichment from Hunter.io." },
};

export default function EnrichmentSettingsPage() {
  const [config, setConfig] = useState<EnrichmentConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingProvider, setEditingProvider] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState("");

  const fetchConfig = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: EnrichmentConfig }>("/crm/enrichment/config");
      setConfig(res.data);
    } catch {
      // Default empty state — no providers configured
      setConfig({
        providers: [
          { id: "clearbit", name: "Clearbit", description: PROVIDER_INFO["clearbit"]?.description ?? "", isConfigured: false, isEnabled: false, priority: 1, usageThisMonth: 0, monthlyLimit: null },
          { id: "apollo", name: "Apollo.io", description: PROVIDER_INFO["apollo"]?.description ?? "", isConfigured: false, isEnabled: false, priority: 2, usageThisMonth: 0, monthlyLimit: null },
          { id: "hunter", name: "Hunter.io", description: PROVIDER_INFO["hunter"]?.description ?? "", isConfigured: false, isEnabled: false, priority: 3, usageThisMonth: 0, monthlyLimit: null },
        ],
        autoEnrich: false,
        enrichOnCreate: true,
        enrichOnImport: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSaveKey = async (e: FormEvent, providerId: string) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      await apiFetch(`/crm/enrichment/providers/${providerId}/configure`, {
        method: "PUT",
        body: JSON.stringify({ apiKey: apiKey.trim() }),
      });
      setSuccess(`${providerId} configured successfully`);
      setEditingProvider(null);
      setApiKey("");
      await fetchConfig();
    } catch {
      setError(`Failed to configure ${providerId}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleProvider = async (providerId: string, enabled: boolean) => {
    try {
      await apiFetch(`/crm/enrichment/providers/${providerId}/toggle`, {
        method: "PUT",
        body: JSON.stringify({ enabled }),
      });
      await fetchConfig();
    } catch {
      setError("Failed to update provider");
    }
  };

  const handleToggleSetting = async (key: "autoEnrich" | "enrichOnCreate" | "enrichOnImport", value: boolean) => {
    try {
      await apiFetch("/crm/enrichment/config", {
        method: "PUT",
        body: JSON.stringify({ [key]: value }),
      });
      setConfig((prev) => prev ? { ...prev, [key]: value } : prev);
    } catch {
      setError("Failed to update setting");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-24" />)}
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="max-w-2xl space-y-8">
      <div className="flex items-center gap-3">
        <Sparkles className="h-5 w-5 text-[var(--color-ai)]" />
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Contact Enrichment</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Auto-fill contact and company data from external providers
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-4 py-3 text-sm text-[var(--color-success)]">
          {success}
        </div>
      )}

      {/* Enrichment settings */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Settings2 className="h-4 w-4 text-[var(--color-muted-foreground)]" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Settings</h2>
        </div>
        <div className="space-y-2">
          {[
            { key: "enrichOnCreate" as const, label: "Enrich on contact creation", desc: "Auto-enrich when a new contact is created" },
            { key: "enrichOnImport" as const, label: "Enrich on CSV import", desc: "Auto-enrich contacts during bulk import" },
            { key: "autoEnrich" as const, label: "Background auto-enrich", desc: "Periodically enrich contacts missing data" },
          ].map(({ key, label, desc }) => (
            <label key={key} className="flex items-center justify-between rounded-lg border border-[var(--color-border)] p-3">
              <div>
                <p className="text-sm font-medium text-[var(--color-foreground)]">{label}</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">{desc}</p>
              </div>
              <input
                type="checkbox"
                checked={config[key]}
                onChange={(e) => handleToggleSetting(key, e.target.checked)}
                className="rounded"
              />
            </label>
          ))}
        </div>
      </section>

      {/* Provider waterfall */}
      <section>
        <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-1">Provider Waterfall</h2>
        <p className="text-sm text-[var(--color-muted-foreground)] mb-4">
          Providers are tried in order. If one returns no results, the next is tried.
        </p>
        <div className="space-y-3">
          {config.providers
            .sort((a, b) => a.priority - b.priority)
            .map((provider, idx) => (
              <div key={provider.id} className="stagger-item">
                {idx > 0 && (
                  <div className="flex justify-center py-1">
                    <ArrowDown className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                  </div>
                )}
                <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-ai)]/10 text-sm font-bold text-[var(--color-ai)]">
                        {provider.priority}
                      </span>
                      <div>
                        <p className="font-medium text-[var(--color-foreground)]">{provider.name}</p>
                        <p className="text-xs text-[var(--color-muted-foreground)]">{provider.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {provider.isConfigured ? (
                        <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" />
                      ) : (
                        <XCircle className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                      )}
                      {provider.isConfigured && (
                        <input
                          type="checkbox"
                          checked={provider.isEnabled}
                          onChange={(e) => handleToggleProvider(provider.id, e.target.checked)}
                          className="rounded"
                        />
                      )}
                    </div>
                  </div>

                  {/* Usage */}
                  {provider.isConfigured && provider.monthlyLimit && (
                    <div className="mb-2">
                      <div className="flex justify-between text-xs text-[var(--color-muted-foreground)]">
                        <span>{provider.usageThisMonth} / {provider.monthlyLimit} this month</span>
                        <span>{Math.round((provider.usageThisMonth / provider.monthlyLimit) * 100)}%</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-[var(--color-surface)] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[var(--color-primary)] transition-all"
                          style={{ width: `${Math.min((provider.usageThisMonth / provider.monthlyLimit) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Configure / Edit key */}
                  {editingProvider === provider.id ? (
                    <form onSubmit={(e) => handleSaveKey(e, provider.id)} className="flex gap-2 mt-2">
                      <div className="relative flex-1">
                        <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-muted-foreground)]" />
                        <input
                          type="password"
                          autoComplete="off"
                          placeholder="API key..."
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] pl-9 pr-3 py-2 text-sm text-[var(--color-foreground)]"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isSaving}
                        className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm text-white hover:opacity-90 disabled:opacity-50"
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditingProvider(null); setApiKey(""); }}
                        className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                      >
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingProvider(provider.id)}
                      className="mt-2 text-sm text-[var(--color-primary)] hover:underline"
                    >
                      {provider.isConfigured ? "Update API key" : "Configure API key"}
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}
